import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import Carousel from '../components/Carousel';
import { Tag } from 'lucide-react';
import { toast } from 'sonner';
import SuppliersMarquee from '../components/SuppliersMarquee';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const carouselImages = [
  '/loja1.jpeg',
  '/loja2.jpeg',
  '/loja4.jpeg',
  '/loja5.jpeg',
  '/loja6.jpeg'
];

const suppliers = [
  { name: "3M", logo: "/fornecedores/3M.svg", href: "" },
  { name: "Amanco", logo: "/fornecedores/amanco.png", href: "" },
  { name: "Belzer", logo: "/fornecedores/belzer.png", href: "" },
  { name: "Blukit", logo: "/fornecedores/blukit.png", href: "" },
  { name: "Bosch", logo: "/fornecedores/bosch.png", href: "" },
  { name: "Ciser", logo: "/fornecedores/ciser.svg", href: "" },
  { name: "Fame", logo: "/fornecedores/fame.png", href: "" },
  { name: "Gedore", logo: "/fornecedores/gedore.png", href: "" },
  { name: "Gerdau", logo: "/fornecedores/gerdau.png", href: "" },
  { name: "Irwin", logo: "/fornecedores/irwin.png", href: "" },
  { name: "Jimo", logo: "/fornecedores/Jimo.png", href: "" },
  { name: "Lorenzetti", logo: "/fornecedores/lorenzetti.png", href: "" },
  { name: "Papaiz", logo: "/fornecedores/papaiz.png", href: "" },
  { name: "Stam", logo: "/fornecedores/stam.png", href: "" },
  { name: "Stanley", logo: "/fornecedores/stanley.png", href: "" },
  { name: "Starrett", logo: "/fornecedores/starrett.png", href: "" },
  { name: "Tigre", logo: "/fornecedores/tigre.png", href: "" },
  { name: "Tramontina", logo: "/fornecedores/tramontina.png", href: "" },
  { name: "TramontinaPro", logo: "/fornecedores/TramontinaPro.png", href: "" },
  { name: "WD-40", logo: "/fornecedores/wd-40.svg", href: "" },
];

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [weeklyOffers, setWeeklyOffers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const productsSectionRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory, searchQuery]);

  const loadData = async () => {
    try {
      const [categoriesRes, offersRes, featuredRes] = await Promise.all([
        axios.get(`${API_URL}/categories?active_only=true`),
        axios.get(`${API_URL}/offers?active_only=true`),
        axios.get(`${API_URL}/products?featured_only=true&active_only=true&limit=8`)
      ]);

      setCategories(categoriesRes.data);
      setWeeklyOffers(offersRes.data);
      setFeaturedProducts(featuredRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('active_only', 'true');
      params.append('limit', '2000');

      if (selectedCategory) {
        params.append('category_id', selectedCategory);
      }

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await axios.get(`${API_URL}/products?${params}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId === selectedCategory ? null : categoryId);
  };

  const scrollToProductsSection = () => {
    if (productsSectionRef.current) {
      const headerOffset = 110;
      const elementPosition =
        productsSectionRef.current.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const selectedCategoryData = categories.find(
    (cat) => cat.id === selectedCategory
  );

  const getOfferProduct = (offer) => {
    return products.find((product) => product.id === offer.product_id);
  };

  return (
    <div className="App">
      <Header onSearch={setSearchQuery} onStartSearch={scrollToProductsSection} />

      {/* Carousel da Loja */}
      <section className="py-0 bg-slate-50">
        <Carousel images={carouselImages} autoPlayInterval={5000} />
      </section>

      <main>
        {/* 1. Weekly Offers Section */}
        {weeklyOffers.length > 0 && (
          <section className="py-8 md:py-12" style={{ backgroundColor: '#273e82' }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 mb-6">
                <Tag style={{ color: '#fef000' }} size={28} />
                <h2
                  className="text-3xl font-bold text-white"
                  style={{ fontFamily: 'Manrope' }}
                >
                  Ofertas da Semana
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {weeklyOffers.map((offer) => {
                  const product = getOfferProduct(offer);

                  if (!product) return null;

                  const mainImage =
                    product.images && product.images.length > 0
                      ? product.images[0].image_url
                      : 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?w=400';

                  const hidePrice = !!product.omit_price;
                  const hasDiscount =
                    !hidePrice &&
                    product.promo_price != null &&
                    product.price != null &&
                    product.promo_price < product.price;

                  const displayPrice = hasDiscount
                    ? product.promo_price
                    : product.price;

                  return (
                    <a
                      key={offer.id}
                      href={`/produto/${product.slug}`}
                      className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 block"
                      data-testid={`offer-card-${offer.id}`}
                    >
                      <div className="h-[320px] overflow-hidden bg-slate-100">
                        <img
                          src={mainImage}
                          alt={product.name || offer.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="p-4">
                        <h3
                          className="font-bold text-lg"
                          style={{ color: '#273e82' }}
                        >
                          {product.name}
                        </h3>

                        {product.reference && (
                          <p className="text-sm text-slate-500 mt-1">
                            Ref: {product.reference}
                          </p>
                        )}

                        <div className="mt-3">
                          {hidePrice ? (
                            <p className="font-semibold text-slate-600 text-sm">
                              Adicione ao carrinho e consulte o preço
                            </p>
                          ) : displayPrice != null ? (
                            hasDiscount ? (
                              <div>
                                <p className="line-through text-slate-400 text-sm">
                                  R$ {Number(product.price).toFixed(2)}
                                </p>
                                <p
                                  className="font-bold text-lg"
                                  style={{ color: '#273e82' }}
                                >
                                  R$ {Number(product.promo_price).toFixed(2)}
                                </p>
                              </div>
                            ) : (
                              <p
                                className="font-bold text-lg"
                                style={{ color: '#273e82' }}
                              >
                                R$ {Number(displayPrice).toFixed(2)}
                              </p>
                            )
                          ) : null}
                        </div>

                        <p className="text-sm text-slate-600 mt-3">
                          Válido até{' '}
                          {new Date(offer.end_date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* 2. Categories */}
        <section className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-2xl font-bold mb-6"
              style={{ color: '#273e82', fontFamily: 'Manrope' }}
            >
              Categorias
            </h2>

            <div className="flex flex-wrap gap-3" data-testid="categories-grid">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`
                    w-[150px]
                    h-[50px]
                    p-1
                    rounded-xl
                    flex
                    items-center
                    justify-center
                    text-center
                    transition-all
                    border
                    flex-shrink-0
                    ${selectedCategory === category.id
                      ? 'bg-[#273e82] text-white border-[#fef000]'
                      : 'bg-[#fef000] text-[#273e82] border-[#fef000] hover:bg-[#273e82] hover:text-white'
                    }
                  `}
                  data-testid={`category-chip-${category.slug}`}
                >
                  <span className="text-[12px] leading-[14px] font-medium break-words">
                    {category.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* 3. Featured Products */}
        {featuredProducts.length > 0 && !searchQuery && !selectedCategory && (
          <section className="py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2
                className="text-3xl md:text-4xl font-bold mb-8"
                style={{ color: '#273e82', fontFamily: 'Manrope' }}
              >
                Produtos em Destaque
              </h2>

              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
                data-testid="featured-products"
              >
                {featuredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 4. Products Grid */}
        <section className="py-16" ref={productsSectionRef}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2
                className="text-3xl font-bold"
                style={{ color: '#273e82', fontFamily: 'Manrope' }}
              >
                {selectedCategoryData
                  ? selectedCategoryData.name
                  : searchQuery
                    ? `Resultados para "${searchQuery}"`
                    : 'Todos os Produtos'}
              </h2>

              {selectedCategory && (
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="text-sm text-slate-600 hover:text-[#273e82] font-medium"
                >
                  Limpar filtro
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#273e82]"></div>
              </div>
            ) : products.length > 0 ? (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
                data-testid="products-grid"
              >
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16" data-testid="no-products">
                <p className="text-slate-600 text-lg">
                  {searchQuery
                    ? 'Nenhum produto encontrado para sua busca.'
                    : 'Nenhum produto disponível.'}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>

      <SuppliersMarquee
        suppliers={suppliers}
        speedSeconds={28}
        logoHeight={56}
      />

      <Footer />
    </div>
  );
};

export default HomePage;